import styled from 'styled-components'
import { desktop, mobile } from '../../styles/media'
import { StyledImage } from './name/ZoomableImage'
import Link from '../router/Link'
import HavesOnImage from '../collection/HavesOnImage'
import Rarity from './Rarity'
import { StyledReleaseDate } from '../text/ReleaseDate'
import { StyledCardPriceAmount } from '../price/CardPriceAmount'
import Name from './name/Name'
import { StyledBetterButton } from '../button/BetterButton'
import { StyledHoverActions } from '../list/HoverAction/HoverActions'
import { StyledHoverAction } from '../list/HoverAction/HoverAction'
import { StyledPrintDropDown } from './PrintDropDown'

export const CardTile = styled(Link)`
	display: grid;
	position: relative;

	> * {
		grid-area: 1 / 1 / 2 / 2;
	}

	> div {
		padding: 0.25rem;

		${mobile`
			padding: 0.125rem;
		`}
	}

	picture img {
		margin-right: 0;
		height: 100%;
		width: 100%;
		max-width: 160px;
	}

	${StyledImage} svg {
		position: absolute;
		top: 0;
		left: 0;

		height: 100%;
		width: 100%;
	}

  	${HavesOnImage} {
		left: 4px;
		bottom: 0.75rem;
	  	top: auto;

	  	${mobile`
	  		left: 2px;
	  		bottom: 0.45rem;
	  	`}
	}

	${Rarity} {
		position: absolute;
		top: -10px;
		right: 4px;
		z-index: 1;

		${mobile`
			top: -12px;
			right: 2px;
		`}
	}

  	${props => props.gray && `
  		filter: grayscale(100%);
  		-webkit-transform: translateZ(0);
		-webkit-perspective: 1000;
		-webkit-backface-visibility: hidden;
  	`}
`

export const PrintListRelativeWrapper = styled.div`
	position: relative;

	${desktop`
		${props => props.showHoverActions && `
			&:hover {
				${StyledHoverActions}{
					background: ${props.selected ? props.theme.primaryVeryLight : props.theme.backgroundVeryLight};

					${StyledHoverAction}, ${StyledPrintDropDown} {
						opacity: 1;
					}
				}

				${StyledPrintDropDown} {
					&:after {
						content: "";
						display: block;

						position: absolute;
						top: 0;
						left: 100%;
						height: 47px;
						width: 100%;
					}
				}
			}
		`}
	`}
`

export const Remark = styled.div`
	background: ${props => props.primary ? props.theme.primary : (props.theme.id === 'dark' ? props.theme.backgroundLight : props.theme.backgroundDark)};
	border-radius: 6px;
	color: ${props => props.theme.white};
	font-size: 0.6rem;
	font-weight: bold;
	padding: 0.25rem 0.35rem;
	text-transform: uppercase;
	width: fit-content;

	align-items: center;
	display: flex;
	gap: 0.25rem;

	${props => props.theme.id === 'dark' && !props.primary && !props.artwork && !props.prerelease && `
		color: #CCC;
	`}

	${props => props.artwork && `
		background: ${props.theme.sage};
	`}

	${props => props.prerelease && `
		background: ${props.theme.orange};
	`}

	${props => props.green && `
		background: ${props.theme.green};
	`}

	${props => props.red && `
		background: ${props.theme.red};
	`}

	${props => props.light && `
		background: ${props.theme.backgroundLight};
		color: ${props.theme.textLight};
	`}
`


export const PrintList = styled.div`
	display: flex;
	flex-direction: column;
	margin-bottom: 1rem;

	> a:not(${CardTile}):not(${StyledBetterButton}), > button, > ${PrintListRelativeWrapper} > a {
		align-items: center;
		display: flex;

		border-bottom: 1px solid ${props => props.theme.backgroundLight};
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		text-decoration: none;
	}

	> a:not(${CardTile}):not(${StyledBetterButton}):first-of-type, > button:first-of-type, > ${PrintListRelativeWrapper}:first-of-type > a {
		border-radius: 2px 2px 0 0;
	}

	> a:not(${CardTile}):not(${StyledBetterButton}):last-of-type, > button:last-of-type, > ${PrintListRelativeWrapper}:last-of-type > a {
		border-bottom: 0;
	}

	${Remark} {
		margin-left: 0.5rem;
	}

	${props => props.loading && `
		cursor: wait;
		opacity: 0.5;
		pointer-events: none;
	`}

	${props => props.view === 'tile' && props.showRarity && `
		> a, > button, > ${PrintListRelativeWrapper} > a {
			padding-top: 0.5rem;
			padding-bottom: 0.5rem;
		}
	`}

	${desktop`
		> a, > button, > ${PrintListRelativeWrapper} > a {
			cursor: pointer;
			padding-right: 0.5rem;
		}

		> a:not(${StyledBetterButton}):hover, > button:hover, > ${PrintListRelativeWrapper}:hover > a {
			border-radius: 2px;
			color: ${props => props.theme.text};
		}

		> a:not(${CardTile}):not(${StyledBetterButton}):hover, > button:hover, > ${PrintListRelativeWrapper}:hover > a {
			background: ${props => props.theme.backgroundVeryLight};
			color: ${props => props.theme.text};
		}

		${props => props.view === 'tile' && `
			display: grid;
			grid-template-columns: repeat(10, 1fr);

			${props.correctOuterPadding ? `
				margin-left: -0.25rem;
				width: calc(100% + 0.5rem);
			` : ''}

			> a, > button {
				transition: all 0.1s;

				img {
					border-radius: 2px;
				}
			}

			> a:hover, > button:hover {
				transform: scale(1.05);
			}
		`}
	`}

	${mobile`
		${props => props.view === 'tile' && `
			display: grid;
			grid-template-columns: repeat(6, 1fr);

			${props.correctOuterPadding ? `
				margin-left: -0.125rem;
				width: calc(100% + 0.25rem);
			` : ''}
		`}
	`}
`

export const Number = styled.div`
	color: ${props => props.theme.textSlightlyLight};

	align-items: center;
	display: flex;

	> svg {
		margin-right: 0.35rem;
	}

	${Name} + & {
		margin-left: 0.5rem;
	}

	${mobile`
		font-size: 0.9rem;
	`}

	${props => props.isFirstPrint && `
		color: ${props.theme.primary};
	`}

	${props => props.prerelease && `
		color: ${props.theme.orange};
	`}

	${props => props.unknown && `
		color: ${props.theme.backgroundSolid};
	`}

	${desktop`
		${StyledCardPriceAmount} + ${StyledReleaseDate} {
			margin-left: 0.5rem;
		}
	`}
`
